/**
 * AN ADDRESS INSIDE THIS APP, FOLLOWED IN PLACE - the seam for a screen that sends the reader to an
 * address it BUILT: a new draft's editor, or the terms interstitial with the way back in its query.
 *
 * WHY A SECOND SEAM BESIDE THE SUPPORT SCREENS' `Navigate`. That one (`../support/navigation.ts`)
 * takes only the addresses the support feature names, typed `CabinetRoute`, and a computed address
 * such as `/articles/art_0192f3ff/edit` is none of them. With no seam to reach, "Start a new draft"
 * (`routes/articles/ArticlesRoute.tsx`) kept a default of `window.location.assign`, and the router
 * renders that screen with NO props - so every new draft was a full page load: a second Firebase
 * boot and a second `GET /v1/me` for one click inside the cabinet, the defect
 * `./RouterNavigateProvider.tsx` records for the support screens. Found 2026-09-23.
 *
 * THE DEFAULT IS STILL THAT FULL PAGE LOAD, with the support seam's own argument for it (its
 * `hardNavigate`, `../support/navigation.ts`): the cabinet owns the whole of
 * `authors.moonx.app` and its Caddy fragment answers every path with the SPA, so the load is
 * correct and only slower. A screen rendered with no router - a spec, a preview - keeps it; the
 * app's root route binds this seam to the router (`./RouterNavigateProvider.tsx`), so in the app
 * every call moves in place.
 */
import { createContext, useContext } from 'react';
import type { MouseEvent } from 'react';

export interface NavigateToHrefOptions {
  /**
   * Take the place of the current history entry, so Back skips it - for an address that was only
   * ever a way through, such as a video opened at the article editor's address.
   */
  readonly replace?: boolean;
}

/** Moves the reader to a same-origin path, query included - `/terms?next=%2Fdashboard`. */
export type NavigateToHref = (href: string, options?: NavigateToHrefOptions) => void;

const fullPageLoad: NavigateToHref = (href, options) => {
  if (options?.replace === true) {
    window.location.replace(href);
  } else {
    window.location.assign(href);
  }
};

export const NavigateToHrefContext = createContext<NavigateToHref>(fullPageLoad);

/** The app's in-app navigation: the router under the app, a full page load without one. */
export function useNavigateToHref(): NavigateToHref {
  return useContext(NavigateToHrefContext);
}

/**
 * True only for a plain primary click nothing has handled yet; any other is the browser's - a new
 * tab, a copied address, a middle click. The ONE copy in this app: the sidebar (`./CabinetShell.tsx`)
 * and every in-app link take it from here. It lives here and not in the shell because a SCREEN
 * cannot import the shell: the shell reads the sidebar's list (`./cabinet-navigation.ts`), that
 * list is computed from `CABINET_ROUTES` at module load, and `CABINET_ROUTES` imports every screen -
 * so the cycle would find the list uninitialised. This file imports nothing of the app's.
 */
export function followsInApp(event: MouseEvent<HTMLElement>): boolean {
  return !(
    event.defaultPrevented ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  );
}
